import { inject, EnvironmentInjector } from '@angular/core';
import { Auth, authState, signOut } from '@angular/fire/auth';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { AuthState, AuthUser, SidebarCategory } from '../interfaces/auth-state.interface';
import { LoginCamposolService } from 'src/app/features/auth/log-in/services/login-camposol.service';
import { LoginFirebaseService } from 'src/app/features/auth/log-in/services/login-firebase.service';
import { LogsService } from '../services/logs.service';
import { filter, firstValueFrom, take } from 'rxjs';
import { computed, effect } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';

const initialState: AuthState = {
    user: null,
    token: localStorage.getItem('auth_token'),
    status: 'init',
    lastActivity: Number(localStorage.getItem('auth_last_activity')) || null,
    menuData: null,
    isLoginInProgress: false,
};

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutos

export const AuthStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed(({ user }) => ({
        accessIds: computed(() => {
            const userData = user();
            if (!userData || !userData.accesos) return [];
            const ids = userData.accesos.map(acceso => acceso.split('-')[0]);
            const uniqueIds = [...new Set(ids)];
            return uniqueIds;
        }),
        clientSessionId: computed(() => user()?.client_session_id ?? null),
    })),
    withMethods((
        store,
        camposolService = inject(LoginCamposolService),
        firebaseAuth = inject(Auth),
        loginFirebaseService = inject(LoginFirebaseService),
        envInjector = inject(EnvironmentInjector),
        logsService = inject(LogsService),
        messageService = inject(MessageService),
    ) => ({

        updateActivity: () => {
            const now = Date.now();
            patchState(store, { lastActivity: now });
            localStorage.setItem('auth_last_activity', now.toString());
        },

        setToken: (token: string | null) => {
            patchState(store, { token });
            if (token) {
                localStorage.setItem('auth_token', token);
            } else {
                localStorage.removeItem('auth_token');
            }
        },

        setLoginInProgress: (value: boolean) => {
            patchState(store, { isLoginInProgress: value });
        },

        /**
         * Guarda el estado de autenticación completo tras un login exitoso.
         * Llamado por el componente al finalizar el flujo secuencial.
         */
        setAuthData: (user: AuthUser, token: string, menuData: SidebarCategory[] | null) => {
            const now = Date.now();
            patchState(store, {
                user,
                token,
                status: 'authenticated',
                menuData,
                lastActivity: now,
            });
            localStorage.setItem('auth_token', token);
            localStorage.setItem('auth_last_activity', now.toString());
            localStorage.setItem('auth_user', JSON.stringify(user));
            if (menuData) {
                localStorage.setItem('auth_menu', JSON.stringify(menuData));
            } else {
                localStorage.removeItem('auth_menu');
            }
        },

        logout: async () => {
            const sessionId = store.user()?.client_session_id;
            const email = store.user()?.email;

            if (sessionId && email) {
                try {
                    const result = await firstValueFrom(logsService.registerSessionClose(sessionId, email));
                } catch {
                    messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Problemas con el cierre de sesión',
                    });
                    return;
                }
            }

            await signOut(firebaseAuth);
            patchState(store, {
                user: null,
                token: null,
                status: 'unauthenticated',
                lastActivity: null,
                isLoginInProgress: false,
            });
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_last_activity');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_menu');
            console.log("se limpio la sesion");
        },

        /**
         * Restaura la sesión cuando el usuario recarga la página con una sesión Firebase activa.
         * Si existe caché en localStorage (user + menu), restaura desde ahí sin llamar al API.
         * Solo hace petición al API de Camposol si no hay caché (primera carga o después de logout).
         * No se llama durante el flujo de login normal (isLoginInProgress lo bloquea).
         */
        loadUserProfile: async () => {
            patchState(store, { status: 'loading' });
            try {
                if (!loginFirebaseService.isAuthReady()) {
                    await firstValueFrom(
                        toObservable(loginFirebaseService.isAuthReady, { injector: envInjector }).pipe(
                            filter(ready => ready),
                            take(1)
                        )
                    );
                }

                const devToken = loginFirebaseService.firebaseIdToken();
                if (!devToken) {
                    throw new Error('Token de autenticación no disponible.');
                }

                const storedUser = localStorage.getItem('auth_user');
                const storedMenu = localStorage.getItem('auth_menu');

                if (storedUser) {
                    const user: AuthUser = JSON.parse(storedUser);
                    const menuData: SidebarCategory[] | null = storedMenu ? JSON.parse(storedMenu) : null;
                    const now = Date.now();

                    patchState(store, {
                        user,
                        token: devToken,
                        status: 'authenticated',
                        menuData,
                        lastActivity: now,
                    });

                    localStorage.setItem('auth_token', devToken);
                    localStorage.setItem('auth_last_activity', now.toString());
                    return;
                }

                const response = await firstValueFrom(camposolService.sendFirebaseTokenToCamposol());

                if (response.status === 'success') {
                    const rawUser = response.data as any;
                    const roles = rawUser.roles || [];
                    const allAccess = [...new Set(roles.flatMap((r: any) => r.accesos || []))] as string[];
                    const allRolesNames = roles.map((r: any) => r.rol).join(', ');

                    const normalizedUser: AuthUser = {
                        ...rawUser,
                        rol: allRolesNames || rawUser.rol || '',
                        accesos: allAccess.length > 0 ? allAccess : (rawUser.accesos || []),
                    };

                    let menuData: SidebarCategory[] | null = null;
                    try {
                        const menuResponse = await firstValueFrom(camposolService.getSidebarMenu());
                        menuData = menuResponse.items;
                    } catch (menuError) {
                        console.warn('[loadUserProfile] No se pudo cargar el menú:', menuError);
                    }

                    const now = Date.now();
                    patchState(store, {
                        user: normalizedUser,
                        token: devToken,
                        status: 'authenticated',
                        menuData,
                        lastActivity: now,
                    });

                    localStorage.setItem('auth_token', devToken);
                    localStorage.setItem('auth_last_activity', now.toString());
                    localStorage.setItem('auth_user', JSON.stringify(normalizedUser));
                    if (menuData) {
                        localStorage.setItem('auth_menu', JSON.stringify(menuData));
                    }
                } else {
                    await signOut(firebaseAuth);
                    patchState(store, { user: null, status: 'unauthenticated' });
                }
            } catch (error) {
                console.error('[loadUserProfile] Error al restaurar sesión:', error);
                await signOut(firebaseAuth);
                patchState(store, { user: null, status: 'unauthenticated' });
            }
        },

        checkInactivity: (): boolean => {
            const lastActivity = store.lastActivity();
            if (!lastActivity) return false;
            return Date.now() - lastActivity > INACTIVITY_TIMEOUT;
        },

        hasPermission: (moduleId: string, action: string): boolean => {
            const user = store.user();
            if (!user || !user.accesos) return false;
            return user.accesos.includes(`${moduleId}-${action}`);
        },
    })),
    withHooks({
        onInit(store) {
            const firebaseAuth = inject(Auth);

            // Monitoreo de actividad: registra/desregistra listeners según el estado
            effect(() => {
                const status = store.status();
                const updateActivity = () => store.updateActivity();
                const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

                if (status === 'authenticated') {
                    events.forEach(event => window.addEventListener(event, updateActivity));
                }

                return () => {
                    events.forEach(event => window.removeEventListener(event, updateActivity));
                };
            });

            // Escucha cambios en la sesión de Firebase.
            // Solo actúa en reconexiones (recarga de página con sesión activa).
            // Durante el login activo, el componente maneja el flujo y este hook es ignorado.
            authState(firebaseAuth).subscribe(async (user) => {
                if (user) {
                    // El componente de login está manejando el flujo: no interferir
                    if (store.isLoginInProgress()) return;

                    if (store.checkInactivity()) {
                        await store.logout();
                        return;
                    }

                    await store.loadUserProfile();
                } else {
                    patchState(store, {
                        user: null,
                        token: null,
                        status: 'unauthenticated',
                        lastActivity: null,
                    });
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('auth_last_activity');
                    localStorage.removeItem('auth_user');
                    localStorage.removeItem('auth_menu');
                }
            });
        }
    })
);
